import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { EntityOption, ENTITY_FETCHERS } from './entity-fetchers';

interface EntityFilterSelectProps {
  field: string;                               // 'offer_id' | 'network_id' | ...
  value: EntityOption[];                        // selected {id,name}
  onChange: (items: EntityOption[]) => void;
  label: string;
  // Venus-style compact display: no chips — the selection collapses to a single
  // comma-joined text summary when unfocused. Set false for classic chips.
  tagsAsText?: boolean;
  sx?: object;
}

/**
 * Async, server-searched multi-select for entity filters. Debounces the typed
 * term and fetches a small page via the entity service's `search` (ilike) param,
 * so it scales past the first N rows. Selected options are always kept in the
 * list so their labels survive across searches. By default renders selections as
 * a compact comma-joined text summary (tagsAsText) rather than a pile of chips.
 */
export default function EntityFilterSelect({
  field, value, onChange, label, tagsAsText = true, sx,
}: EntityFilterSelectProps) {
  const fetcher = ENTITY_FETCHERS[field];
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');

  // Debounce the typed term (250ms)
  useEffect(() => {
    const t = setTimeout(() => setSearch(input.trim()), 250);
    return () => clearTimeout(t);
  }, [input]);

  const { data: fetched, isFetching } = useQuery({
    queryKey: ['entity-filter', field, search],
    queryFn: () => fetcher({ search: search || undefined, limit: 20 }).then((r) => r.data.map((x) => ({ id: x.id, name: x.name }))),
    enabled: !!fetcher,
    staleTime: 30_000,
  });

  // Union of fetched results + currently-selected (so selected chips keep names)
  const options = useMemo(() => {
    const map = new Map<string, EntityOption>();
    for (const v of value) map.set(v.id, v);
    for (const o of fetched || []) map.set(o.id, o);
    return Array.from(map.values());
  }, [fetched, value]);

  return (
    <Autocomplete
      multiple
      size="small"
      sx={{
        ...sx,
        // Keep the collapsed text summary on a single line with an ellipsis.
        ...(tagsAsText && {
          '& .MuiAutocomplete-tag': {
            maxWidth: 'calc(100% - 48px)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }),
      }}
      options={options}
      value={value}
      loading={isFetching}
      filterOptions={(x) => x}              // server does the searching
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onInputChange={(_, v, reason) => { if (reason !== 'reset') setInput(v); }}
      onChange={(_, items) => onChange(items as EntityOption[])}
      limitTags={tagsAsText ? 0 : 2}
      // tagsAsText: render all selected names as one comma-joined summary instead of chips.
      getLimitTagsText={tagsAsText ? () => value.map((v) => v.name).join(', ') : undefined}
      disableCloseOnSelect
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={value.length ? '' : 'All'} />
      )}
    />
  );
}
